import { useState } from "react";

import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { FolderOpenIcon, PlusCircleIcon } from "@phosphor-icons/react/dist/ssr";
import { doc, updateDoc } from "firebase/firestore";

import {
  ExampleModal,
  RegenerateModal,
  TabHeader,
} from "@/components/Luna/features";
import CreateExampleModal from "@/components/Luna/features/CreateExampleModal";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { Example } from "@/types";
import { db } from "@/utils/firebase";

import { ExampleItem, ExampleSkeleton } from "./index";

const Examples = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateExampleModalOpen, setIsCreateExampleModalOpen] =
    useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [selectedExampleIndex, setSelectedExampleIndex] = useState<
    number | null
  >(null);
  const { lessonInteractionProfile } = useLesson();
  const [isEditing, setIsEditing] = useState(false);
  const { aiAxios } = useFirebaseContext();
  const examples = lessonInteractionProfile?.realtimeResources?.examples || [];
  const sortedExamples = [...examples].reverse();

  const handleGenerateExample = async (prompt?: string) => {
    if (!lessonInteractionProfile) return;
    setIsGenerating(true);
    aiAxios
      .post("luna/examples", {
        lessonId: lessonInteractionProfile.id,
        prompt: prompt || "",
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const handleApproveExample = async (index: number) => {
    if (!lessonInteractionProfile) return;
    const exampleToApprove =
      lessonInteractionProfile?.realtimeResources?.examples[index];
    if (!exampleToApprove) return;

    try {
      const lessonRef = doc(
        db,
        "lessonInteractionProfiles",
        lessonInteractionProfile.id
      );
      await updateDoc(lessonRef, {
        realtimeResources: {
          ...lessonInteractionProfile.realtimeResources,
          examples: lessonInteractionProfile.realtimeResources?.examples.map(
            (example, i) =>
              i === index ? {
                ...example,
                isApproved: true
              } : example
          ),
        },
      });
    } catch (error) {
      console.error("Error approving example:", error);
    }
  };

  const handleRegenerateExample = async (prompt: string) => {
    if (regeneratingIndex === null || !lessonInteractionProfile) return;
    const exampleToRegenerate =
      lessonInteractionProfile?.realtimeResources?.examples[regeneratingIndex];
    if (!exampleToRegenerate) return;
    aiAxios
      .put("luna/examples", {
        lessonId: lessonInteractionProfile?.id,
        exampleId: exampleToRegenerate.id,
        prompt: prompt,
      })
      .finally(() => {
        setIsRegenerateModalOpen(false);
        setRegeneratingIndex(null);
      });
  };

  const handleDeleteExample = async (index: number) => {
    if (confirm("Are you sure you want to delete this example?")) {
      const exampleToDelete =
        lessonInteractionProfile?.realtimeResources?.examples[index];
      if (!exampleToDelete) return;

      try {
        const lessonRef = doc(
          db,
          "lessonInteractionProfiles",
          lessonInteractionProfile.id
        );
        await updateDoc(lessonRef, {
          realtimeResources: {
            ...lessonInteractionProfile.realtimeResources,
            examples:
              lessonInteractionProfile.realtimeResources?.examples.filter(
                (_, i) => i !== index
              ),
          },
        });
      } catch (error) {
        console.error("Error deleting example:", error);
      }
    }
  };

  const handleOpenModal = (index: number) => {
    setSelectedExampleIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExampleIndex(null);
    setIsEditing(false);
  };

  const handleUpdateExample = async (
    index: number,
    updatedExample: Example
  ) => {
    if (!lessonInteractionProfile) return;
    const lessonRef = doc(
      db,
      "lessonInteractionProfiles",
      lessonInteractionProfile.id
    );
    await updateDoc(lessonRef, {
      realtimeResources: {
        ...lessonInteractionProfile.realtimeResources,
        examples: lessonInteractionProfile.realtimeResources?.examples.map(
          (example, i) => (i === index ? updatedExample : example)
        ),
      },
    });
  };

  return (
    <Box
      width="100%"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <TabHeader
          icon={<FolderOpenIcon size={16} weight="fill" />}
          title="Examples"
          handleGenerate={() => {
            handleGenerateExample();
          }}
          isGenerating={isGenerating}
          actions={
            <Tooltip title="Create Manually" arrow>
              <IconButton
                disabled={isGenerating}
                onClick={() => !isGenerating && setIsCreateExampleModalOpen(true)}
                sx={{
                  p: 0.75,
                  border: "1px solid",
                  borderColor: "secondary.main",
                  borderRadius: 1.5,
                  color: "text.primary",
                  "&.Mui-disabled": {
                    color: "text.disabled",
                    borderColor: "divider",
                  },
                }}
              >
                <PlusCircleIcon size={16} weight="fill" />
              </IconButton>
            </Tooltip>
          }
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0, // Ensures the Box can shrink properly in a flex column
        }}
      >
        {isGenerating && <ExampleSkeleton />}
        {examples.length < 1 && !isGenerating ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 4,
            }}
          >
            <Typography
              variant="text15B"
              color="textSecondary"
              textAlign="center"
              sx={{ width: "100%" }}
            >
              Clarify concepts with examples
            </Typography>
            <Typography
              variant="text13L"
              color="textSecondary"
              textAlign="center"
              sx={{
                width: "100%",
                opacity: 0.8
              }}
            >
              Collaborate with Luna to generate custom examples that help visualize complex topics for your student.
            </Typography>
          </Box>
        ) : (
          <Box>
            {sortedExamples.map((example) => {
              const originalIndex = examples.findIndex(
                (e) => e.id === example.id
              );
              return regeneratingIndex === originalIndex ? (
                <ExampleSkeleton key={example.id} />
              ) : (
                <ExampleItem
                  key={example.id}
                  example={example}
                  index={originalIndex}
                  onApprove={handleApproveExample}
                  onRegenerate={() => {
                    setRegeneratingIndex(originalIndex);
                    setIsRegenerateModalOpen(true);
                  }}
                  onEdit={() => {
                    setIsEditing(true);
                    setSelectedExampleIndex(originalIndex);
                    setIsModalOpen(true);
                  }}
                  onOpenModal={handleOpenModal}
                  onDelete={handleDeleteExample}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {regeneratingIndex !== null && (
        <RegenerateModal
          isOpen={isRegenerateModalOpen}
          onClose={() => {
            setIsRegenerateModalOpen(false);
            setRegeneratingIndex(null);
          }}
          onRegenerate={(prompt) => {
            setIsRegenerateModalOpen(false);
            handleRegenerateExample(prompt);
          }}
        />
      )}

      {selectedExampleIndex !== null && (
        <ExampleModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          example={examples[selectedExampleIndex]}
          index={selectedExampleIndex}
          isEditing={isEditing}
          onUpdate={(index, example) => {
            handleUpdateExample(index, example);
            handleCloseModal();
          }}
          onApprove={() => {
            handleApproveExample(selectedExampleIndex);
            handleCloseModal();
          }}
          onEdit={() => setIsEditing(true)}
          onRegenerate={() => {
            setIsRegenerateModalOpen(true);
            setRegeneratingIndex(selectedExampleIndex);
            handleCloseModal();
          }}
          onDelete={() => {
            handleDeleteExample(selectedExampleIndex);
            handleCloseModal();
          }}
          onCancelEdit={() => setIsEditing(false)}
        />
      )}

      <CreateExampleModal
        isOpen={isCreateExampleModalOpen}
        onClose={() => setIsCreateExampleModalOpen(false)}
        onCreate={handleGenerateExample}
      />
    </Box>
  );
};

export default Examples;
